'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo, useState } from 'react';
import { LocalizedHtml, useI18n } from '@udemy-v2/i18n';
import { Button } from '@udemy-v2/react-core-components';
import { Duration } from '@udemy-v2/i18n';
import { Accordion, TextSkeleton } from '@udemy-v2/react-reveal-components';
import { publishEvent, TrackImpression } from '@udemy-v2/event-tracking';
import { CurriculumSection } from './curriculum-section.react-component';
import { Curriculum as CurriculumType } from '@udemy-v2/browse-course';
import { CurriculumImpressionEvent, ClickEvent, LearningProduct } from '../../events/events';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import styles from './curriculum.module.scss';
const DISPLAYED_SECTIONS_COUNT = 10;
interface CurriculumProps {
  titleTypography?: 'ud-heading-lg' | 'ud-heading-xl';
  enableExpandAllControl?: boolean;
  practiceIncentiveCardsComponent?: React.ReactNode;
}
interface CurriculumContentInfoProps {
  curriculum: CurriculumType;
}
export const CurriculumContentInfo = React.memo(t0 => {
  const $ = _c(7);
  const {
    curriculum
  } = t0;
  const {
    gettext,
    ninterpolate
  } = useI18n();
  const {
    durationInSeconds,
    isPracticeTestCourse
  } = useClpCourse();
  let t1;
  if ($[0] !== curriculum.contentCounts || $[1] !== curriculum.sections || $[2] !== durationInSeconds || $[3] !== gettext || $[4] !== isPracticeTestCourse || $[5] !== ninterpolate) {
    const {
      lecturesCount,
      practiceTestQuestionsCount
    } = curriculum.contentCounts || {};
    t1 = isPracticeTestCourse ? <span className={styles["content-length"]}>{ninterpolate("%(count)s question", "%(count)s questions", practiceTestQuestionsCount || 0, {
        count: practiceTestQuestionsCount || 0
      })}</span> : <span className={styles["content-length"]}>{ninterpolate("%(sectionCount)s section", "%(sectionCount)s sections", curriculum.sections.length, {
        sectionCount: curriculum.sections.length
      })}{" \u2022 "}{ninterpolate("%(lectureCount)s lecture", "%(lectureCount)s lectures", lecturesCount || 0, {
        lectureCount: lecturesCount || 0
      })}{durationInSeconds && <>{" \u2022 "}<LocalizedHtml html={gettext("<span class=\"contentLength\">%(contentLength)s</span> total length")} interpolate={{
          contentLength: <Duration numSeconds={durationInSeconds || 0} precision="minutes" presentationStyle="humanCompact" />
        }} /></>}</span>;
    $[0] = curriculum.contentCounts;
    $[1] = curriculum.sections;
    $[2] = durationInSeconds;
    $[3] = gettext;
    $[4] = isPracticeTestCourse;
    $[5] = ninterpolate;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
});
CurriculumContentInfo.displayName = 'CurriculumContentInfo';
export const Curriculum = t0 => {
  const $ = _c(46);
  const {
    titleTypography: t1,
    enableExpandAllControl: t2,
    practiceIncentiveCardsComponent: t3
  } = t0;
  const titleTypography = t1 === undefined ? "ud-heading-xl" : t1;
  const enableExpandAllControl = t2 === undefined ? true : t2;
  const practiceIncentiveCardsComponent = t3 === undefined ? null : t3;
  const {
    gettext,
    ninterpolate
  } = useI18n();
  const course = useClpCourse();
  const {
    curriculum,
    id: courseId,
    isPracticeTestCourse
  } = course;
  const [areAllSectionsExpanded, setAreAllSectionsExpanded] = useState(undefined);
  const [displayedSectionsCount, setDisplayedSectionsCount] = useState(DISPLAYED_SECTIONS_COUNT);
  let t4;
  if ($[0] !== curriculum?.sections) {
    t4 = () => {
      const sections = curriculum?.sections;
      if (!sections) {
        return;
      }
      setDisplayedSectionsCount(sections.length);
    };
    $[0] = curriculum?.sections;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleShowAllSections = t4;
  let t5;
  if ($[2] !== courseId) {
    t5 = () => {
      publishEvent(new CurriculumImpressionEvent({
        type: LearningProduct.COURSE,
        id: parseInt(courseId, 10),
        trackingId: courseId || ""
      }));
    };
    $[2] = courseId;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const trackImpression = t5;
  let t6;
  if ($[4] !== courseId || $[5] !== handleShowAllSections) {
    t6 = () => {
      handleShowAllSections();
      setAreAllSectionsExpanded(_temp);
      publishEvent(new ClickEvent({
        componentName: "curriculumRead",
        trackingId: courseId,
        relatedObjectType: LearningProduct.COURSE,
        relatedObjectId: parseInt(courseId, 10)
      }));
    };
    $[4] = courseId;
    $[5] = handleShowAllSections;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleToggleAllSections = t6;
  let t7;
  bb0: {
    if (!curriculum) {
      let t8;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <TextSkeleton className={styles["curriculum-loader"]} withTitle={true} lineCountPerParagraph={3} paragraphCount={16} />;
        $[7] = t8;
      } else {
        t8 = $[7];
      }
      t7 = t8;
      break bb0;
    }
    if (!curriculum.sections?.length) {
      t7 = null;
      break bb0;
    }
    const hiddenSectionsCount = curriculum.sections.length - displayedSectionsCount;
    let t8;
    if ($[8] !== areAllSectionsExpanded || $[9] !== curriculum.sections || $[10] !== displayedSectionsCount) {
      let t9;
      if ($[12] !== areAllSectionsExpanded) {
        t9 = (section, index) => <CurriculumSection key={section.title + index} arrayIndex={index} title={section.title} items={section.items} numLectures={section?.lectureCount || 0} contentLength={section?.contentLength || 0} areAllSectionsExpanded={areAllSectionsExpanded} />;
        $[12] = areAllSectionsExpanded;
        $[13] = t9;
      } else {
        t9 = $[13];
      }
      t8 = curriculum.sections.slice(0, displayedSectionsCount).map(t9);
      $[8] = areAllSectionsExpanded;
      $[9] = curriculum.sections;
      $[10] = displayedSectionsCount;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    const sectionComponents = t8;
    const userCanExpandAllSections = enableExpandAllControl && !isPracticeTestCourse && sectionComponents.length > 1;
    const t9 = `${styles["curriculum-header"]} ${titleTypography}`;
    let t10;
    if ($[14] !== gettext || $[15] !== isPracticeTestCourse) {
      t10 = isPracticeTestCourse ? gettext("Included in This Course") : gettext("Course content");
      $[14] = gettext;
      $[15] = isPracticeTestCourse;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] !== t10 || $[18] !== t9) {
      t11 = <h2 className={t9} data-purpose="curriculum-header">{t10}</h2>;
      $[17] = t10;
      $[18] = t9;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    let t12;
    if ($[20] !== curriculum) {
      t12 = <div className="ud-text-sm" data-testid="curriculum-stats"><CurriculumContentInfo curriculum={curriculum} /></div>;
      $[20] = curriculum;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    let t13;
    if ($[22] !== areAllSectionsExpanded || $[23] !== gettext || $[24] !== handleToggleAllSections || $[25] !== userCanExpandAllSections) {
      t13 = userCanExpandAllSections ? <Button data-purpose="expand-toggle" size="medium" udStyle="ghost" aria-expanded={areAllSectionsExpanded} onClick={handleToggleAllSections}>{areAllSectionsExpanded ? gettext("Collapse all sections") : gettext("Expand all sections")}</Button> : null;
      $[22] = areAllSectionsExpanded;
      $[23] = gettext;
      $[24] = handleToggleAllSections;
      $[25] = userCanExpandAllSections;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    let t14;
    if ($[27] !== t12 || $[28] !== t13) {
      t14 = <div className={styles["curriculum-sub-header"]}>{t12}{t13}</div>;
      $[27] = t12;
      $[28] = t13;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    let t15;
    if ($[30] !== sectionComponents) {
      t15 = <Accordion size="medium">{sectionComponents}</Accordion>;
      $[30] = sectionComponents;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    let t16;
    if ($[32] !== handleShowAllSections || $[33] !== hiddenSectionsCount || $[34] !== ninterpolate) {
      t16 = hiddenSectionsCount > 0 && <Button size="medium" udStyle="secondary" className={styles["curriculum-show-more"]} onClick={handleShowAllSections} data-purpose="show-more">{ninterpolate("%s more section", "%s more sections", hiddenSectionsCount)}</Button>;
      $[32] = handleShowAllSections;
      $[33] = hiddenSectionsCount;
      $[34] = ninterpolate;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    let t17;
    if ($[36] !== practiceIncentiveCardsComponent || $[37] !== t11 || $[38] !== t14 || $[39] !== t15 || $[40] !== t16) {
      t17 = <div data-purpose="course-curriculum">{t11}{practiceIncentiveCardsComponent}{t14}{t15}{t16}</div>;
      $[36] = practiceIncentiveCardsComponent;
      $[37] = t11;
      $[38] = t14;
      $[39] = t15;
      $[40] = t16;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    t7 = t17;
  }
  const curriculumSections = t7;
  let t8;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span id="objective" className="in-page-offset-anchor" />;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== curriculumSections || $[44] !== trackImpression) {
    t9 = <div className="component-margin">{t8}<TrackImpression trackFunc={trackImpression} visibilityThreshold={0.25}>{curriculumSections}</TrackImpression></div>;
    $[43] = curriculumSections;
    $[44] = trackImpression;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  return t9;
};
function _temp(prev) {
  return !prev;
}